import { Link, useParams } from "react-router-dom";
import { works } from "@/mocks/works";
import { news } from "@/mocks/news";
import WorkCard from "@/components/base/WorkCard";
import ShareMenu from "@/components/base/ShareMenu";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";

export default function WorkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const work = works.find((w) => w.id === id);

  useSeo({
    title: work ? `《${work.title}》详情 — 天海翼影视作品` : "影视作品详情 — 天海翼影视内容站",
    description: work
      ? `${work.title}（${work.year}）由${work.director}执导，${work.cast.join("、")}主演。${work.summary}`
      : "天海翼影视作品详情页。",
    keywords: work ? `天海翼,${work.title},${work.category},天海翼作品,${work.tags.join(",")}` : "天海翼影视作品",
  });

  if (!work) {
    return (
      <section className="mx-auto max-w-[1280px] px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-file-unknow-line"
          title="没有找到这部作品"
          description="该作品可能已下架或链接有误，返回作品列表看看其他内容吧。"
          actionText="返回影视作品"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/works");
          }}
        />
      </section>
    );
  }

  const relatedNews = news.filter((n) => n.relatedWorkIds.includes(work.id));
  const sameCategory = works.filter((w) => w.id !== work.id && w.category === work.category);
  const fill = works
    .filter((w) => w.id !== work.id && w.category !== work.category)
    .sort((a, b) => b.popularity - a.popularity);
  const recommended = [...sameCategory, ...fill].slice(0, 4);

  const meta = [
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.join("、") },
    { label: "上映时间", value: work.releaseDate },
    { label: "作品类型", value: work.category },
    { label: "片长 / 集数", value: work.duration },
    { label: "地区", value: work.region },
    { label: "作品状态", value: work.status },
  ];

  return (
    <article>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <Link to="/works" className="cursor-pointer hover:text-primary-700">影视作品</Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-700">{work.title}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="flex flex-col gap-6 sm:flex-row">
              <div className="w-full shrink-0 overflow-hidden rounded-lg border border-background-200 bg-secondary-100 sm:w-56">
                <div className="aspect-[3/4] w-full">
                  <img
                    src={work.cover}
                    alt={`${work.title} 封面`}
                    title={`${work.title} — 天海翼影视作品`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs text-primary-700">{work.category}</span>
                  <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">{work.status}</span>
                  {work.rating > 0 ? (
                    <span className="flex items-center gap-1 text-sm text-accent-600">
                      <i className="ri-star-fill" />
                      {work.rating.toFixed(1)}
                    </span>
                  ) : (
                    <span className="text-xs text-foreground-500">评分待更新</span>
                  )}
                </div>
                <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{work.title}</h1>
                <p className="mt-1 text-sm text-foreground-500">
                  {work.year} · {work.region} · {work.duration}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{work.summary}</p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {work.tags.map((tag) => (
                    <span key={tag} className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6">
                  <ShareMenu path={`/works/${work.id}`} title={`天海翼作品《${work.title}》`} />
                </div>
              </div>
            </div>

            <section className="mt-10">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                作品资料
              </h2>
              <dl className="grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200 bg-background-100 p-5 sm:grid-cols-2 md:p-6">
                {meta.map((item) => (
                  <div key={item.label} className="flex gap-3 text-sm">
                    <dt className="w-20 shrink-0 text-foreground-500">{item.label}</dt>
                    <dd className="flex-1 text-foreground-900">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="mt-10">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                简介与剧情
              </h2>
              <div className="space-y-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                {work.synopsis.map((para, index) => (
                  <p key={index}>{para}</p>
                ))}
              </div>
            </section>

            {relatedNews.length > 0 && (
              <section className="mt-10">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <span className="h-5 w-1 rounded-full bg-accent-500" />
                  相关资讯
                </h2>
                <ul className="flex flex-col gap-3">
                  {relatedNews.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/news/${item.id}`}
                        className="group flex cursor-pointer items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                      >
                        <i className="ri-newspaper-line text-lg text-primary-600" />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700 line-clamp-1">
                            {item.title}
                          </span>
                          <span className="text-xs text-foreground-500">{item.date} · {item.type}</span>
                        </span>
                        <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-magic-line text-accent-500" />
                相关推荐
              </h2>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-1">
                {recommended.map((item) => (
                  <Link
                    key={item.id}
                    to={`/works/${item.id}`}
                    className="group flex cursor-pointer gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                  >
                    <div className="h-24 w-16 shrink-0 overflow-hidden rounded-md bg-secondary-100">
                      <img
                        src={item.cover}
                        alt={`${item.title} 封面`}
                        title={`天海翼作品 ${item.title}`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col justify-center">
                      <h3 className="text-sm font-semibold text-foreground-900 transition-colors group-hover:text-primary-700 line-clamp-1">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">{item.year} · {item.category}</p>
                      <p className="mt-1.5 text-xs leading-relaxed text-foreground-600 clamp-2">{item.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-14 border-t border-background-200 pt-10">
          <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950">更多天海翼作品</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {works
              .filter((w) => w.id !== work.id)
              .sort((a, b) => b.popularity - a.popularity)
              .slice(0, 4)
              .map((w, index) => (
                <WorkCard key={w.id} work={w} index={index} />
              ))}
          </div>
        </section>
      </div>
    </article>
  );
}